// DTOs mirrored from Scout.Voice (VoiceProfileService) and the platform's BlackManta.Idiolect
// VoiceDescriptor. Keep in step by hand until the API publishes OpenAPI.

export interface RegisterDimensions {
  involved: number
  narrative: number
  explicit: number
  persuasive: number
  abstract: number
  formal: number
}

export interface Convention {
  text: string
  rate: number
}

export interface VoiceDescriptor {
  register: RegisterDimensions
  greetings: Convention[]
  closings: Convention[]
  length: { medianWords: number; medianWordsReplies: number; medianSentences: number; meanSentenceLength: number }
  contractionRate: number
  punctuation: { exclamationRate: number; questionRate: number; dashRate: number; ellipsisRate: number; semicolonRate: number; parenthesisRate: number }
  signaturePhrases: string[]
  neverSay: string[]
  summary: string | null
  provisional: boolean
  messageCount: number
  /** what the person changed in earlier drafts; the next draft is written under these */
  preferences?: string[] | null
}

/** Who decided a message's audience and intent. `regex` is the cue table's guess from before any model
 *  read the mail — a fallback, and the screen says so rather than showing it as measured. */
export type LabelSource = 'model' | 'regex' | 'you'

export const LABEL_SOURCE_NOTE = 'classified by pattern, not yet sorted'

export interface ExemplarView {
  id: string
  subject: string | null
  sentAt: string
  ownText: string
  audience: string
  intent: string
  /** how many of the person's own words it carries */
  wordCount: number
  /** why this message stands as an example, in one line: labels, length, and whether a model sorted it */
  reason: string
  /** what the person would rather have written; the original in ownText is kept either way */
  rewriteText?: string | null
  rewrittenAt?: string | null
  labelSource?: LabelSource
}

/** One scenario on the index: its count, who decided those labels, and which messages are its examples. */
export interface ScenarioIndexEntry {
  scenario: string
  audience: string
  intent: string
  channel: string
  label: string
  messageCount: number
  modelSorted: number
  regexClassified: number
  youFiled: number
  exemplarIds: string[]
  inProfile: boolean
}

export interface ScenarioIndexView {
  revision: number
  messageCount: number
  scenarios: ScenarioIndexEntry[]
}

/** One message as a scenario's list shows it. `head` is the first of the person's own words. */
export interface ScenarioMessage {
  id: string
  subject: string | null
  sentAt: string
  words: number
  isReply: boolean
  kind: string | null
  labelSource: LabelSource
  head: string
  isExemplar: boolean
  isRewritten: boolean
}

export interface ScenarioMessagesView {
  scenario: string
  label: string
  total: number
  page: number
  take: number
  messages: ScenarioMessage[]
}

/** What a rewrite did: the original is kept, and a large enough rewrite taught the scenario. */
export interface RewriteView {
  messageId: string
  scenario: string
  ownText: string
  rewriteText: string | null
  rewrittenAt: string | null
  editDistance: number
  preferenceNote: string | null
}

export interface ScenarioView {
  scenario: string
  channel: string
  audience: string
  intent: string
  messageCount: number
  descriptor: VoiceDescriptor
  exemplars: ExemplarView[]
  selfCheckScore: number | null
  modelId: string | null
  /** where these examples came from: sorted mail, or the rules' guess before the sort */
  exemplarNote: string
}

export interface VoiceProfileView {
  userId: string
  displayName: string
  address: string
  revision: number
  builtAt: string
  styleModel: string
  styleDimensions: number
  messageCount: number
  provisional: boolean
  modelConfigured: boolean
  overall: ScenarioView
  scenarios: ScenarioView[]
}

export interface MailboxConnection {
  id: string
  spoke: string
  userId: string
  sourceId: string
  catalogType: string
  address: string
  displayName: string
  status: string
  connectedAt: string
  lastSyncAt: string | null
  messageCount: number
  sync: { checkpoint: string | null; readSoFar: number; deltaLink: string | null; windowStart: string | null; sourceSentTotal: number | null; sourceWindowCount: number | null } | null
}

export type ReadState = 'none' | 'never' | 'running' | 'stopped' | 'cancelled' | 'complete' | 'degraded'

/** What the last completed read did, and every gap a step left, in words. */
export interface ReadReceipt {
  read: number
  kept: number
  earliest: string | null
  latest: string | null
  completedAt: string | null
  gaps: string[]
  windowStart: string | null
  /** what the mailbox said the window held; the read is complete only when read reaches it */
  windowExpected: number | null
  /** what the mailbox holds all-time */
  sourceSentTotal: number | null
}

export interface MailboxView {
  connected: boolean
  connection: MailboxConnection | null
  availableCatalogTypes: string[]
  readState: ReadState
  activeBuild: Job | null
  lastBuild: Job | null
  read: ReadReceipt | null
}

export interface ExportedMessage {
  id: string
  subject?: string | null
  body: string
  from: string
  fromName?: string | null
  to?: string[]
  sentAt: string
  isReply?: boolean | null
}

export type JobStatus = 'Pending' | 'Running' | 'Completed' | 'Failed' | 'Cancelled'
export type StepStatus = 'Pending' | 'Running' | 'Completed' | 'Failed' | 'Skipped'

export interface JobStep {
  stepId: string
  label: string
  status: StepStatus
  startedAt: string | null
  completedAt: string | null
  message: string | null
  done: number
  total: number | null
  rate: number | null
  etaSeconds: number | null
  /** things this step could not do and said so; a completed step with warnings left a gap */
  warnings: number
}

export interface Job {
  id: string
  spoke: string
  kind: string
  /** the kind's own request, as JSON (a transcript-extract carries its transcriptId) */
  requestJson?: string
  createdBy: string
  status: JobStatus
  steps: JobStep[]
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  error: string | null
  shapeJson: string | null
  /** sum over steps; a job with warnings completed, but not cleanly */
  warnings: number
}

export interface JobLogLine {
  at: string
  stepId: string
  level: string
  message: string
}

export interface JobView {
  job: Job
  log: JobLogLine[]
}

export interface Verdict {
  score: number
  centroidSimilarity: number
  nearestExemplarSimilarity: number
  profileCohesion: number
  registerDeltas: Record<string, number>
  failing: string[]
  passes: boolean
}

export interface DraftView {
  id: string
  scenario: string
  profileRevision: number
  draftText: string
  verdict: Verdict
  attempts: number
  modelId: string
}

export interface FeedbackView {
  draftId: string
  editedWords: number
  editDistance: number
}

export interface ProfileRevisionSummary {
  revision: number
  builtAt: string
  scenarioCount: number
  messageCount: number
  provisional: boolean
}

export interface VoiceDraft {
  id: string
  scenario: string
  profileRevision: number
  brief: string
  draftText: string
  attempts: number
  finalText: string | null
  editDistance: number | null
  createdAt: string
}

export interface HistoryView {
  revisions: ProfileRevisionSummary[]
  builds: Job[]
  drafts: VoiceDraft[]
}

export interface MailExportFormat {
  id: string
  name: string
  extensions: string[]
  hint: string
}

export interface UploadSummary {
  format: string
  fileName: string
  bytes: number
  parsed: number
  fromYou: number
  /** messages somebody wrote TO them that the same file carried (the Inbox side) */
  toYou: number
  kept: number
  earliest: string | null
  latest: string | null
  connection: MailboxConnection
  /** the build that reads what was just uploaded; the page goes there */
  jobId: string
}
